/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */

import { isEqual, isEmpty, every } from 'lodash';
import { createSelector } from 'reselect';

import {
  isActiveAdvisory,
  isViewOnlyAccount
} from 'services/Products';
import { hasBlockViewAccess, hasStopAll } from 'services/Accounts';
import {
  extractAndFormatOwners,
  isPurgedAccount,
  sortAccountsByName,
  sortAccountsByRetirement,
  isOwnerOnAgreementByBPID
} from 'services/Accounts';
import { getAccounts, getAccountsById } from 'state/account/AccountSelectors';
import { getNonPurgedAccounts } from 'state/account/AccountSelectors';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { formatAccountNumber } from 'services/Formats';
import { GRANT_VIEW_ACCESS_RATE_LIMIT_ERROR, isNotQualified } from 'services/GrantViewAccess';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';

const getGrantViewAccess = (state) => state.grantViewAccess || {};

export const organizeViewAccounts = (accounts, granteeBPID) => {
  let groups = {};

  accounts
    .filter((account) => {
      return defaultGVAFilter(account, granteeBPID);
    })
    .sort((a, b) => {
      return sortAccountsByRetirement(a, b) || sortAccountsByName(a, b);
    })

    .forEach((account) => {
      if (!groups[account.accountType]) {
        groups[account.accountType] = [];
      }
      groups[account.accountType].push({
        ...account,
        owners: extractAndFormatOwners(account),
        accountWChecksum: formatAccountNumber(account)
      });
    });

  return groups;
};

const defaultGVAFilter = (account, granteeBPID) => {
  return (
    !isViewOnlyAccount(account) &&
    !isPurgedAccount(account) &&
    !isActiveAdvisory(account.productType) &&
    !hasBlockViewAccess(account) &&
    !hasStopAll(account) &&
    !isNotQualified(account.accountTypeId) &&
    (!granteeBPID || !isOwnerOnAgreementByBPID(account, granteeBPID))
  );
};

export const isGrantViewAccessAuthorizedAccounts = createSelector(     
  getGrantViewAccess,
  (grantViewAccess) => {
    const accounts = grantViewAccess.addUserAuthorization.accounts;
    return accounts && accounts.length > 0 ? accounts : [];
  }
);

export const isGrantViewAccessAddUserAuthorized = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => grantViewAccess.addUserAuthorization.authorized
);
export const isGrantViewAccessAddUserRequested = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => grantViewAccess.addUserAuthorization.requested
);

export const grantViewAccessAddUserBPID = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => grantViewAccess.granteeBusinessPartyId
);

export const getGrantViewAccessAccounts = createSelector(
  getAccounts,
  grantViewAccessAddUserBPID,
  (accounts, granteeBPID) => organizeViewAccounts(accounts, granteeBPID)
);

export const getGrantViewAccessAccountsForAddNew = createSelector(
  isGrantViewAccessAuthorizedAccounts,
  getAccountsById,
  (accounts, accountsById) => {
    const updatedAccounts = accounts
            .filter((a) => !isEmpty(accountsById[a.accountId]))
            .map((a) => accountsById[a.accountId]);
    return organizeViewAccounts(Object.values(updatedAccounts))}
);

export const getGrantViewRelationships = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => grantViewAccess.viewRelationships || []
);

export const hasGrantViewRelationships = createSelector(
  getGrantViewRelationships,
  (viewRelationships) => viewRelationships && viewRelationships.length > 0
);

const getViewAccountId = (viewAccessAccount) =>
  viewAccessAccount.accountDescriptor.accountId;

const hasMatchingViewAccessAccount = (accountId, viewAccessAccounts) =>
  !!viewAccessAccounts.find(
    (viewAccount) => getViewAccountId(viewAccount) === accountId
  );

const addHasViewAccessToAccounts = (allAccounts, viewAccessAccounts) =>
  allAccounts.map((account) => ({
    ...account,
    hasViewAccess: hasMatchingViewAccessAccount(
      account.accountId,
      viewAccessAccounts
    )
  }));

export const getViewRelationships = createSelector(
  getNonPurgedAccounts,
  getGrantViewRelationships,
  (allAccounts, grantedViewRelationships) =>
    grantedViewRelationships.map(({ customer, viewAccesses }) => ({
      customer,
      accounts: organizeViewAccounts(
        addHasViewAccessToAccounts(allAccounts, viewAccesses),
        customer.businessPartyId
      )
    }))
);

export const getGrantViewAccessDeleteUserStatus = createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'deleteGrantViewAccessUser';
    const deleteStatus = requestStatus[key];
    return deleteStatus;
  }
);
export const isInvalidUserInput = createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'grantViewAccessAddUserAuth';
    const inputstatus = requestStatus[key];
   return inputstatus && inputstatus===REQUEST_STATES.error ? true : false;
  }
);

export const getGrantViewAccessDeleteUserSuccessStatus= createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'deleteGrantViewAccessUser';
    const inputstatus = requestStatus && requestStatus[key];
   return inputstatus && inputstatus===REQUEST_STATES.received;
  }
);

export const getGrantViewAccessDeleteUserFailureStatus= createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'deleteGrantViewAccessUser';
    const inputstatus = requestStatus && requestStatus[key];
   return inputstatus && inputstatus===REQUEST_STATES.error;
  }
);

export const getGrantViewAccessSaveSucess= createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'updateViewRelationships';
    const inputstatus = requestStatus && requestStatus[key];
   return inputstatus && inputstatus===REQUEST_STATES.received;
  }
);
export const getGrantViewAccessAddSaveSuccess= createSelector(
    getRequestStatus,
    (requestStatus) => {
        const key = 'addViewRelationships';
        const inputstatus = requestStatus && requestStatus[key];
        return inputstatus && inputstatus===REQUEST_STATES.received;
    }
);
export const getGrantViewAccessSaveError= createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'updateViewRelationships';
    const inputstatus = requestStatus && requestStatus[key];
   return inputstatus && inputstatus===REQUEST_STATES.error;
  }
);
export const getGrantViewAccessAddSaveError= createSelector(
    getRequestStatus,
    (requestStatus) => {
        const key = 'addViewRelationships';
        const inputstatus = requestStatus && requestStatus[key];
        return inputstatus && inputstatus===REQUEST_STATES.error;
    }
);
export const isGrantViewAccessAddUserReceived = createSelector(
  getRequestStatus,
  (requestStatus) => {
    const key = 'grantViewAccessAddUserAuth';
    const inputstatus = requestStatus[key];
   return inputstatus && inputstatus===REQUEST_STATES.received;
  }
);

export const getActiveSelections = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => grantViewAccess.selections
);

export const getIsActiveSelectionDirty = createSelector(
  getActiveSelections,
  (activeSelections) => !isEmpty(activeSelections)
);

export const getActiveCustomerIndex = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => grantViewAccess.activeCustomerIndex
);

export const getCustomersSelections = createSelector(
  getActiveCustomerIndex,
  getViewRelationships,
  (activeCustomerIndex, viewRelationships) => {
    if (activeCustomerIndex === null) {
      return {};
    }

    const accountGroups = viewRelationships[activeCustomerIndex].accounts;
    return Object.values(accountGroups)
      .flat() // gather all accounts, regardless of type, into a single array
      .reduce(
        (acc, account) => ({
          ...acc,
          [account.accountId]: account.hasViewAccess
        }),
        {}
      );
  }
);
export const getModifiedActiveSelections = createSelector(
  getCustomersSelections,
  getActiveSelections,
  (initialSelections, activeSelections) => {
      const modifiedSelections = Object.entries(activeSelections).reduce(
      (acc, [id, value]) => {
        if (initialSelections[id] !== value) {
          acc[id] = value;
        }
        return acc;
      },
      {}
    );
    return modifiedSelections;
  }
);
export const generateGVAEditPayload = createSelector(
  getModifiedActiveSelections,
  (modifiedActiveSelection) => {
      const editPayLoad = Object.entries(modifiedActiveSelection).reduce(
        (acc, [id, hasAccess]) => ({
          ...acc,
          [id]: { action: hasAccess ? 'add' : 'remove' } // track the change
        }),
        {}
    );
    return editPayLoad;
  }
);

export const getEditSelections = createSelector(
  getCustomersSelections,
  getActiveSelections,
  (customersSelections, activeSelections) => ({
    ...customersSelections,
    ...activeSelections
  })
);

export const getShouldDisableSave = createSelector(
  getEditSelections,
  getCustomersSelections,
  (editedSelections, initialSelections) => {
    return (
      every(editedSelections, (selection) => !selection) ||
      isEqual(editedSelections, initialSelections)
    );
  }
);

export const getIsRateLimitError = createSelector(
    getGrantViewAccess,
    getRequestStatus,
    (grantViewAccess, requestStatus) => {
        const key = 'grantViewAccessAddUserAuth';
        const inputstatus =  requestStatus && requestStatus[key] ? requestStatus[key] : null;
        return inputstatus && grantViewAccess?.error === GRANT_VIEW_ACCESS_RATE_LIMIT_ERROR;
    }
);


export const hasAccountsForViewAccess = createSelector(     
  getGrantViewAccess,
  (grantViewAccess) => {
    const accounts = grantViewAccess.addUserAuthorization.accounts;
    return accounts && accounts.length > 0 ? true : false;
  }
);

export const isNoEligibleAccountsError = createSelector(
  getGrantViewAccess,
  getRequestStatus,
  (grantViewAccess, requestStatus) => {
    const key = 'grantViewAccessAddUserAuth';
    const inputstatus =  requestStatus && requestStatus[key] ? requestStatus[key] : null;
    const accounts = grantViewAccess.addUserAuthorization.accounts;
    return accounts && !accounts.length && inputstatus === REQUEST_STATES.received;
  }
);
export const isgvaEnabledAlready = createSelector(
  getGrantViewAccess,
  (grantViewAccess) => {
    const gvaEnabledAlready =
    grantViewAccess?.addUserAuthorization?.gvaEnabledAlready;
    return gvaEnabledAlready;
  }
);
